/**
 * The staff sidebar: what is on the web today, plus what is coming from the Windows app.
 *
 * Live links keep the exact paths and VisibleTabs gates the old `SidebarNav.js` used, so no
 * screen gains or loses visibility. "Soon" links are Windows-only screens; they all open one
 * page (`/coming-soon/:id`) that says what the screen does and where it is in Windows today.
 *
 * Items & orders, Receivers and Tests are left out on purpose: they matter to one or two
 * organizations, and this menu is shown to every web client.
 */
import { QUERIES_LABEL, QUERIES_PATH, QUERIES_PERMISSION } from '../../Queries/queriesModule'

export type NavLink = {
    id: string
    label: string
    path?: string
    permission?: string
    requiresHub?: boolean
    href?: string // an outside address (opens in a new tab)
    soon?: true
    desc?: string
    win?: string
}

/**
 * A sidebar line. `items` are the screens behind it (2+ become tabs on the page); `sub` are
 * further screens drawn indented right under the line in the sidebar, never as page tabs.
 */
export type NavSection = { id: string; label: string; more?: boolean; items: NavLink[]; sub?: NavLink[] }

export type NavEntry = {
    id: string
    label: string
    icon: string
    permission?: string
    link?: NavLink
    sections?: NavSection[]
}

/** Same address as 'Create Support Ticket' in the user menu (UserDetails.js). */
const SUPPORT_PORTAL_URL = 'https://advanceddata.atlassian.net/servicedesk/customer/portals'

export const COMING_SOON_BASE = '/coming-soon'

export const comingSoonPath = (id: string): string => `${COMING_SOON_BASE}/${id}`

const soon = (id: string, label: string, desc: string, win: string): NavLink => ({ id, label, soon: true, desc, win })

/** A section holding one link, named after it. */
const one = (link: NavLink, more?: boolean): NavSection => ({ id: `${link.id}-s`, label: link.label, items: [link], ...(more ? { more } : {}) })

export const FUTURE_NAV: NavEntry[] = [
    {
        id: 'home',
        label: 'Home',
        icon: 'home',
        link: soon('home', 'Home', 'Your start page: today’s numbers, what needs attention and quick links to the screens you use most.', 'To Do tab (start screen)'),
    },
    { id: 'buki', label: QUERIES_LABEL, icon: 'buki', link: { id: 'buki', label: QUERIES_LABEL, path: QUERIES_PATH, permission: QUERIES_PERMISSION } },
    {
        id: 'todo',
        label: 'To Do',
        icon: 'todo',
        link: soon(
            'todo',
            'To Do',
            'Everything waiting for you: messages between staff, pending actions, scheduled texts, declined cards and payments waiting for approval.',
            'To Do › To Do (the Windows start screen)',
        ),
    },
    { id: 'hub', label: 'Work Hub', icon: 'hub', link: { id: 'hub', label: 'Work Hub', path: '/hub', requiresHub: true } },
    {
        id: 'fundraising',
        label: 'Fundraising',
        icon: 'fundraising',
        sections: [
            // Occasions and Voters sit nested under Donors in the sidebar (DES-21926), not as page tabs.
            {
                id: 'donors-s',
                label: 'Donors',
                items: [{ id: 'donors', label: 'Donors', path: '/donors-list', permission: 'Donors' }],
                sub: [
                    soon('occasions', 'Occasions', 'Yahrzeits, birthdays and other yearly dates, with Hebrew-date repeats and letters.', 'Donors › Occasions'),
                    soon('voters', 'Voters', 'Voters imported from the voter file and matched to donors and spouses.', 'Donors › Wizards › Match Voters to Donors, and the Voters tab on the donor card'),
                ],
            },
            {
                id: 'pledges-s',
                label: 'Pledges',
                items: [
                    { id: 'pledges', label: 'Single', path: '/pledges-list', permission: 'Pledges' },
                    { id: 'rec-pledges', label: 'Recurring', path: '/rec-pledges-list', permission: 'Pledges_Reccuring' },
                ],
            },
            {
                id: 'payments-s',
                label: 'Payments',
                items: [
                    { id: 'payments', label: 'Single', path: '/paymentslist', permission: 'Payments' },
                    { id: 'rec-payments', label: 'Recurring', path: '/rec-payments-list', permission: 'RecurringPayment' },
                ],
            },
            {
                id: 'accounts-s',
                label: 'Accounts',
                items: [
                    soon('accounts', 'Accounts', 'The bank and card-processing accounts money goes into, with totals per account.', 'Payments › Accounts'),
                    soon('transactions', 'Donor transactions', 'Money a donor keeps on account with you: deposits, withdrawals and the current credit.', 'Donors › Transactions'),
                ],
            },
            // Appeals (Shuls, Appeals, Calendar) is left out of the menu for now (DES-21926).
            {
                id: 'fund-setup-s',
                label: 'Setup',
                items: [
                    soon('payment-lists', 'Financial', 'The choices behind fundraising money fields.', 'Payments › Misc › Misc Payments'),
                    soon('donor-lists', 'Donor', 'The choices behind donor fields.', 'Donors › Misc'),
                ],
            },
        ],
    },
    {
        id: 'school',
        label: 'School',
        icon: 'school',
        permission: 'School',
        sections: [
            one({ id: 'students', label: 'Students', path: '/students-list', permission: 'School_StudentList' }),
            one({ id: 'parents', label: 'Parents', path: '/parents-list', permission: 'School_Parents' }),
            {
                id: 'school-charges-s',
                label: 'School charges',
                items: [
                    { id: 'school-charges', label: 'Single', path: '/school-charges', permission: 'School_Charges' },
                    { id: 'school-rec-charges', label: 'Recurring', path: '/school-recurring-charges', permission: 'School_RecurringCharges' },
                ],
            },
            {
                id: 'school-payments-s',
                label: 'School payments',
                items: [
                    { id: 'school-payments', label: 'Single', path: '/school-payments', permission: 'School_Payments' },
                    { id: 'school-rec-payments', label: 'Recurring', path: '/school-recurring-payments', permission: 'SchoolRecurringPayment' },
                ],
            },
            {
                id: 'attendance-s',
                label: 'Attendance',
                items: [
                    soon('attendance', 'Daily', 'Daily attendance for every class.', 'School › Misc › Attendance'),
                    soon('late-notes', 'Late notes', 'Late notes for students who came in late.', 'School › Misc › Late Note'),
                    soon('session-attendance', 'Sessions', 'Attendance per class session.', 'Header shortcut › Session Attendance'),
                ],
            },
            one({ id: 'registration', label: 'Registration', path: '/registration', permission: 'School' }),
            one(soon('school-lists', 'Setup', 'Choices for school fields: classes, grades and similar.', 'School › Misc › School Misc')),
            one(soon('report-cards', 'Report cards', 'Marks and report cards per term.', 'School › Misc › Report Cards'), true),
            one(soon('remedial', 'Remedial', 'Remedial tasks for students who need extra help.', 'School › Misc › Remedial'), true),
            one(soon('sessions', 'Sessions & schedule', 'Class sessions, their timings and the schedule.', 'School › Sessions'), true),
            one(soon('bus-routes', 'Bus routes', 'Bus routes, stops and which students ride each bus.', 'School › Misc › Bus Routes'), true),
            one(soon('employees', 'Employees & jobs', 'Teachers and staff, and their jobs.', 'School › Employees'), true),
        ],
    },
    {
        id: 'communication',
        label: 'Communication',
        icon: 'communication',
        sections: [
            {
                id: 'messages-s',
                label: 'Messages',
                items: [
                    soon('send-messages', 'Send', 'Send one message to a list of donors or parents by email, voice call or text.', 'Reports menu › Send Emails, Voice Calls and SMS'),
                    soon('communications', 'Communications', 'A log of calls, letters, emails and texts with each donor.', 'Donors › Communications'),
                    soon('sms-replies', 'Text replies', 'Texts donors sent back.', 'To Do › Letters › SMS Replies'),
                ],
            },
            {
                id: 'letters-s',
                label: 'Letters',
                items: [
                    soon('pending-letters', 'Pending', 'Thank-you, reminder and overdue letters waiting to print or send.', 'To Do › Letters › Pending Letters'),
                    soon('sent-letters', 'Sent', 'Letters already sent, by donor and date.', 'To Do › Letters › Sent Letters'),
                ],
            },
            {
                id: 'reminders-s',
                label: 'Reminders & notes',
                items: [
                    { id: 'reminders', label: 'Reminders', path: '/reminders-list', permission: 'Donors_Reminders' },
                    soon('rec-reminders', 'Recurring reminders', 'Reminders that repeat on a schedule.', 'Donors › Recurring Reminders'),
                    soon('notes', 'Notes', 'Simple notes about a donor or parent: text, date and who wrote it.', 'Donors › Reminders › Reminders and Notes'),
                ],
            },
        ],
    },
    {
        id: 'reports',
        label: 'Reports',
        icon: 'reports',
        sections: [
            {
                id: 'reports-s',
                label: 'Reports',
                items: [
                    soon('reports', 'Saved reports', 'All saved reports in one place.', 'To Do › Reports'),
                    soon('quick-reports', 'Quick reports', 'Reports built from saved queries.', 'To Do › Misc › Quick Reports'),
                    soon('report-wizard', 'Report wizard', 'Build a data report step by step.', 'To Do › Misc › Report Wizard'),
                ],
            },
            {
                id: 'queries-s',
                label: 'Queries',
                items: [
                    soon('saved-queries', 'Saved queries', 'Saved data queries you can run and export.', 'To Do › Queries'),
                    soon('query-builder', 'Query builder', 'Build a query by picking fields, with no code.', 'To Do › Query Builder'),
                    soon('auto-reports', 'Automatic reports', 'Reports that run and send themselves on a schedule.', 'To Do › Automatic Query Reports'),
                ],
            },
        ],
    },
    {
        id: 'import',
        label: 'Import / Export',
        icon: 'import',
        sections: [
            {
                id: 'import-s',
                label: 'Import',
                items: [
                    soon('import', 'Import wizard', 'Import donors, payments and more from a file.', 'To Do › Misc › Import'),
                    soon('carriers', 'Phone carriers', 'Look up the phone company for numbers, so texts can be sent.', 'To Do › Misc › Carriers'),
                ],
            },
            one(soon('export', 'Export', 'Take donors, payments, pledges, parents or any list out to Excel, all from one place.', 'Every list › Export, and To Do › Queries › Export list')),
        ],
    },
    {
        id: 'tools',
        label: 'Tools & wizards',
        icon: 'tools',
        sections: [
            {
                id: 'tools-s',
                label: 'Tools & wizards',
                items: [
                    soon('tools', 'All wizards', 'One searchable page with every wizard, grouped by Fundraising, School and General.', 'The Wizards menus on the Donors, Pledges, Payments and School screens, and To Do › Misc'),
                    soon('merge-donors', 'Merge duplicates', 'Find donors entered twice and merge them.', 'Donors › Misc › Duplicates'),
                    soon('deceased-wizard', 'Deceased', 'Record a person who passed away, add the family in one go, see payments made in their memory and send letters.', 'Donors › Wizards › Deceased'),
                    soon('delete-donors', 'Delete donors', 'Delete donors from the database.', 'Donors › Wizards › Delete Donors from the DB'),
                ],
            },
            {
                id: 'fund-wiz',
                label: 'Fundraising',
                items: [
                    soon('fund-merge-donors', 'Merge duplicates', 'Find donors entered twice and merge them.', 'Donors › Misc › Duplicates'),
                    soon('fund-deceased-wizard', 'Deceased', 'Record a person who passed away, add the family in one go, see payments made in their memory and send letters.', 'Donors › Wizards › Deceased'),
                    soon('fund-delete-donors', 'Delete donors', 'Delete donors from the database.', 'Donors › Wizards › Delete Donors from the DB'),
                    soon('delete-pledges', 'Delete pledges', 'Delete pledges from the database, with a review list first.', 'Pledges › Delete Pledges from the DB'),
                    soon('delete-rec-pledges', 'Delete recurring pledges', 'Delete recurring pledges from the database.', 'Pledges › Delete Rec. Pledges from the DB'),
                    soon('check-wizard', 'Fast entry (checks)', 'Enter a stack of checks, cash, card or ACH payments quickly, one after another.', 'Donors › Wizards › Check, and Payments › Misc › Split Payments'),
                    soon('scan-checks', 'Scan checks', 'Scan many checks and turn them into payments.', 'Payments › Scan Multiple Checks'),
                    soon('apply-payments', 'Apply payments to pledges', 'Apply unapplied payments to open pledges in bulk.', 'Payments › Apply Payments to Pledges'),
                    soon('sync-payments', 'Sync online payments', 'Bring in payments made through payment links and Cardknox.', 'Payments › Sync Payments from Payment Link / Sync Payments from Cardknox'),
                    soon('delete-payments', 'Delete payments', 'Delete payments from the database, with a review list first.', 'Payments › Delete Payments from the DB'),
                    soon('delete-reminders', 'Delete reminders', 'Delete old reminders in bulk.', 'Donors › Wizards › Delete Reminders from the DB'),
                ],
            },
            {
                id: 'school-wiz',
                label: 'School',
                items: [
                    { ...soon('enrollment', 'Enrollment', 'Enroll many students at once, and move everyone up a grade at the end of the year.', 'School › Wizards › Enrollment Switch, Mass Enrollment, Promote or Graduate Students'), permission: 'School' },
                    { ...soon('assign-class', 'Assign to class', 'Put many students into a class at once.', 'School › Wizards › Assign to Class'), permission: 'School' },
                    { ...soon('change-class-info', 'Change class info', 'Change class details for many students at once.', 'School › Wizards › Add/Change Class Info for Multiple Students'), permission: 'School' },
                    { ...soon('assign-bus-routes', 'Assign bus routes', 'Put students on bus routes in bulk.', 'School › Wizards › Assign Students to Bus Routes'), permission: 'School' },
                    { ...soon('update-health', 'Update health info', 'Update health details for many students.', 'School › Wizards › Update Student Health Info'), permission: 'School' },
                    { ...soon('charge-many', 'Charge many', 'One charge for many students or parents.', 'School › Wizards › Charge for Multiple Students/Parents'), permission: 'School' },
                    { ...soon('rec-charge-many', 'Recurring charge for many', 'One recurring charge for many students or parents.', 'School › Wizards › Recurring Charge for Multiple Students/Parents'), permission: 'School' },
                    { ...soon('charge-upgrade', 'Charge upgrade', 'Raise or change existing charges in bulk.', 'School › Wizards › Charge & Recurring Charge Upgrade Wizard'), permission: 'School' },
                    { ...soon('remedial-charging', 'Remedial charging', 'Charge parents for remedial services.', 'School › Wizards › Remedial Charging'), permission: 'School' },
                    { ...soon('add-parents-from-donors', 'Add parents from donors', 'Make parents from existing donor records.', 'School › Wizards › Add parents from donors'), permission: 'School' },
                    { ...soon('delete-students', 'Delete students', 'Delete students from the database.', 'School › Wizards › Delete Students from the DB'), permission: 'School' },
                    { ...soon('delete-parents', 'Delete parents', 'Delete parents from the database.', 'School › Wizards › Delete Parents from the DB'), permission: 'School' },
                ],
            },
        ],
    },
    { id: 'settings', label: 'Settings', icon: 'settings', link: { id: 'settings', label: 'Settings', path: '/settings' } },
    {
        id: 'help',
        label: 'Help',
        icon: 'help',
        sections: [
            one(soon('tutorials', 'Tutorials', 'Short videos and guides.', 'To Do › Tutorials and Requests › Tutorials')),
            one({ id: 'features', label: 'Requested Features', path: '/new-features' }),
            one({ id: 'support', label: 'Create support ticket', href: SUPPORT_PORTAL_URL }),
            one(soon('about', 'About & licence', 'App version, licence and which modules your organization has turned on.', 'File › About and Register')),
        ],
    },
]

export const linkHref = (link: NavLink): string => link.href ?? (link.soon || !link.path ? comingSoonPath(link.id) : link.path)

const allLinks = (nav: NavEntry[]): NavLink[] =>
    nav.flatMap((e) => (e.link ? [e.link] : (e.sections ?? []).flatMap((s) => [...s.items, ...(s.sub ?? [])])))

/** Where a sidebar section line goes: its first link (call it on a filtered section, so the first VISIBLE one). */
export const sectionHref = (section: NavSection): string => linkHref(section.items[0])

/**
 * The section whose tabs belong on top of this page: one with 2+ links, one of which IS this page.
 * Exact match only, so a detail page such as /pledges-list/123 gets no tabs.
 */
export const findTabsFor = (nav: NavEntry[], pathname: string): NavSection | undefined =>
    nav
        .flatMap((e) => e.sections ?? [])
        .find((s) => s.items.length > 1 && s.items.some((l) => linkHref(l) === pathname))

/** The sidebar section a link sits in (its group name for page titles). */
export const findSectionOf = (id: string): NavSection | undefined =>
    FUTURE_NAV.flatMap((e) => e.sections ?? []).find((s) => [...s.items, ...(s.sub ?? [])].some((l) => l.id === id))

export const findSoonLink = (id: string): NavLink | undefined => allLinks(FUTURE_NAV).find((l) => l.id === id && l.soon)

export const filterNav = (nav: NavEntry[], ctx: { visibleTabs: string[] | undefined; allowHub: boolean }): NavEntry[] => {
    const tabs = ctx.visibleTabs ?? []
    const shows = (l: NavLink) => (!l.permission || tabs.includes(l.permission)) && (!l.requiresHub || ctx.allowHub)
    const out: NavEntry[] = []
    for (const entry of nav) {
        if (entry.permission && !tabs.includes(entry.permission)) continue
        if (entry.link) {
            if (shows(entry.link)) out.push({ ...entry })
            continue
        }
        const sections = (entry.sections ?? [])
            // A section shows only if one of its own items does; its sub links go with it.
            .map((s) => ({ ...s, items: s.items.filter(shows), ...(s.sub ? { sub: s.sub.filter(shows) } : {}) }))
            .filter((s) => s.items.length > 0)
        if (sections.length > 0) out.push({ ...entry, sections })
    }
    return out
}
